import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { joinWaitlist, getWaitlistStatus, getAlphaSystemStatus, redeemInviteCode } from '../../api/alpha';
import InviteCodeInput from '../common/InviteCodeInput';
import { Footer } from '../layout';
import { useFirebase } from '../../context/FirebaseContext';
import { useSelector } from 'react-redux';
import './WaitlistPage.css';
import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import Seo from '../common/Seo';

const WaitlistPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { auth } = useFirebase();
  const currentUser = useSelector((state) => state.user.currentUser);

  const [name, setName] = useState('');
  const [referralCode] = useState(searchParams.get('ref') || '');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadSystemStatus();
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      if (!currentUser?.emailVerified) {
        return;
      }

      const result = await getWaitlistStatus(auth);
      if (result && !result.error) {
        setStatus(result);
      }
    };

    loadStatus();
  }, [auth, currentUser]);

  const loadSystemStatus = async () => {
    const result = await getAlphaSystemStatus();
    setSystemStatus(result);
  };

  const handleJoinWaitlist = async () => {
    if (!currentUser) {
      setError('Please sign in to join the waitlist');
      return;
    }

    if (!currentUser.emailVerified) {
      setError('Please verify your email before joining the waitlist');
      return;
    }

    if (systemStatus && !systemStatus.waitlistEnabled) {
      setError('The waitlist is currently closed');
      return;
    }

    setLoading(true);
    setError(null);
    logEvent(analytics, 'waitlist_join_attempt', { has_referral: !!referralCode });

    try {
      const result = await joinWaitlist(auth, name, referralCode);

      if (result.success || result.errorCode === 'ALREADY_EXISTS') {
        logEvent(analytics, 'waitlist_join_success', { position: result.position, has_referral: !!referralCode });
        const statusResult = await getWaitlistStatus(auth);
        if (statusResult && !statusResult.error) {
          setStatus(statusResult);
        } else {
          setStatus({
            position: result.position,
            referralCode: result.referralCode,
            referralLink: result.referralLink,
            referralCount: 0,
            status: 'waiting'
          });
        }
        return;
      }

      switch (result.errorCode) {
        case 'EMAIL_NOT_VERIFIED':
          setError('Please verify your email before joining the waitlist');
          break;
        case 'WAITLIST_DISABLED':
          setError('The waitlist is currently closed');
          break;
        case 'NOT_AUTHENTICATED':
          setError('Please sign in to join the waitlist');
          break;
        default:
          setError('Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      logEvent(analytics, 'waitlist_join_failed', { error: 'unknown' });
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralLink = () => {
    if (status?.referralLink) {
      navigator.clipboard.writeText(status.referralLink);
      logEvent(analytics, 'waitlist_referral_copied', { position: status.position });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleValidCode = async (code, validation) => {
    if (currentUser) {
      // Try to redeem the code immediately
      setLoading(true);
      setError(null);

      try {
        const result = await redeemInviteCode(auth, code);

        if (result.success) {
          logEvent(analytics, 'invite_code_redeemed', { success: true });
          // Success! Force a hard redirect to home to refresh auth state and load the app
          window.location.href = '/';
          return;
        } else {
          logEvent(analytics, 'invite_code_redeemed', { success: false, error: result.errorCode });
          // Show error message
          const errorMessages = {
            'INVALID': 'Invalid invite code',
            'EXPIRED': 'This code has expired',
            'MAX_USES': 'This code has reached its usage limit',
            'WAVE_FULL': 'This wave is currently full',
            'WAVE_INACTIVE': 'This wave is not active',
            'DEACTIVATED': 'This code has been deactivated',
          };
          setError(errorMessages[result.errorCode] || 'Failed to redeem code. Please try again.');
        }
      } catch (err) {
        console.error('Error redeeming code:', err);
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Navigate to signup with the code
    navigate(`/signup?code=${encodeURIComponent(code)}`);
  };

  const statusLabel = status?.status
    ? status.status.charAt(0).toUpperCase() + status.status.slice(1)
    : null;
  const isInvited = status?.status === 'invited';
  const isConverted = status?.status === 'converted';

  // User is on the waitlist - show status
  if (status) {
    return (
      <div className="waitlist-page">
        <Seo
          title="Waitlist Status - NanoTradingCopilot"
          description="Check your status on the NanoTradingCopilot waitlist. Share your referral link to skip the line."
        />
        <Box className="waitlist-container">
          <Card className="waitlist-card">
            <CardContent>
              <Box className="waitlist-header">
                <CheckCircleIcon className="success-icon" />
                <Typography variant="h4" component="h1">
                  {isInvited ? "You're approved!" : isConverted ? 'Access granted' : "You're on the list!"}
                </Typography>
              </Box>

              {statusLabel && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Chip label={statusLabel} color={isInvited ? 'success' : 'primary'} />
                </Box>
              )}

              {isInvited && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  You’ve been approved! Check your email for your invite code. Enter it below to unlock access immediately.
                </Alert>
              )}
              {isConverted && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Your account has access. You can start using the app now.
                </Alert>
              )}

              <Box className="waitlist-stats">
                <Box className="stat-item">
                  <Typography variant="h3" className="stat-number">
                    #{status.position}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Your Position
                  </Typography>
                </Box>
                <Box className="stat-item">
                  <Typography variant="h3" className="stat-number">
                    {status.totalAhead || status.position - 1}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    People Ahead
                  </Typography>
                </Box>
                <Box className="stat-item">
                  <Typography variant="h3" className="stat-number">
                    {status.referralCount || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Referrals
                  </Typography>
                </Box>
              </Box>

              <Divider className="waitlist-divider" />

              <Box className="referral-section">
                <Typography variant="h6" gutterBottom>
                  <ShareIcon fontSize="small" /> Skip the line!
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Share your referral link and move up when friends join:
                </Typography>

                <Box className="referral-link-box">
                  <TextField
                    value={status.referralLink || ''}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                          <IconButton onClick={handleCopyReferralLink} size="small">
                            {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Divider className="waitlist-divider" />

              <Box className="have-code-section">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {isInvited ? "Enter your invite code to start using the app:" : "Already have an invite code?"}
                </Typography>
                {showCodeInput || isInvited ? (
                  <Box mt={2}>
                    <InviteCodeInput onValidCode={handleValidCode} />

                  </Box>
                ) : (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setShowCodeInput(true)}
                  >
                    Enter code
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button
                variant="outlined"
                color="secondary"
                size="medium"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
              >
                Sign out
              </Button>
            </CardContent>
          </Card>
        </Box>
        <Footer />
      </div>
    );
  }

  // Initial waitlist signup form
  return (
    <div className="waitlist-page">
      <Seo
        title="Join the Waitlist - NanoTradingCopilot"
        description="Get early access to NanoTradingCopilot. Join the waitlist for our private alpha and start validating your trades."
        keywords="forex waitlist, early access trading tool, trading copilot alpha"
      />
      <Box className="waitlist-container">
        <Card className="waitlist-card">
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              Join the Alpha Waitlist
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              NanoTradingCopilot is currently in private alpha. Join the waitlist to get early access.
            </Typography>

            {!currentUser && (
              <Alert severity="info" className="referral-notice">
                Create an account or sign in to join the waitlist.
              </Alert>
            )}

            {currentUser && systemStatus && !systemStatus.waitlistEnabled && (
              <Alert severity="warning" className="error-notice">
                The waitlist is currently closed. Please check back soon.
              </Alert>
            )}

            {referralCode && (
              <Alert severity="success" className="referral-notice">
                You were referred by a friend! You'll start higher on the list.
              </Alert>
            )}

            {error && (
              <Alert severity="error" className="error-notice">
                {error}
              </Alert>
            )}

            {currentUser ? (
              <Box className="waitlist-form">
                <TextField
                  label="Signed in as"
                  value={currentUser.email || ''}
                  fullWidth
                  variant="outlined"
                  disabled
                />
                <TextField
                  label="Name (optional)"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={handleJoinWaitlist}
                  disabled={loading || (systemStatus && !systemStatus.waitlistEnabled)}
                >
                  {loading ? 'Joining...' : 'Add me to the waitlist'}
                </Button>

                <Divider sx={{ my: 2 }} />

                <Button
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  fullWidth
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                >
                  Sign out to use different account
                </Button>
              </Box>
            ) : (
              <Box className="waitlist-form">
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                >
                  Create an account
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth
                >
                  Sign in
                </Button>
              </Box>
            )}

            <Divider className="waitlist-divider" />

            <Box>
              <Typography variant="h6" gutterBottom>
                How it works
              </Typography>
              <Typography variant="body2" color="textSecondary">
                1) Sign up and verify your email.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                2) Join the waitlist with the button above.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                3) Share your referral link to move up the queue.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                4) Enter your code to unlock access immediately.
              </Typography>
            </Box>

            <Divider className="waitlist-divider" />

            <Box className="have-code-section">
              <Typography variant="body2" color="textSecondary">
                Already have an invite code?
              </Typography>
              {!showCodeInput ? (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowCodeInput(true)}
                >
                  Enter code
                </Button>
              ) : (
                <Box mt={2}>
                  <InviteCodeInput onValidCode={handleValidCode} />

                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Footer />
    </div>
  );
};

export default WaitlistPage;
